import React from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Alert, Box, Button, Paper, Typography, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, Stack,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import ApartmentIcon from '@mui/icons-material/Apartment'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'
import StatusBadge from '../../../components/ui/StatusBadge'

Show.layout = (page) => <AppShell>{page}</AppShell>

const CATEGORY_LABELS = { residential: 'Wohnung', commercial: 'Gewerbe', parking: 'Stellplatz', other: 'Sonstiges' }
const MANAGEMENT_TYPE_LABELS = { weg: 'WEG', mv: 'MV', sev: 'SEV', privat: 'Privat' }

export default function Show({ property, units = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}

  const activeUnits = units.filter((u) => u.active)

  return (
    <Box>
      <PageHeader
        title={property.name}
        breadcrumbs={[]}
        actions={
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/edit`)}
            data-testid="edit-property-btn"
          >
            Bearbeiten
          </Button>
        }
      />

      {property.management_type === 'weg' && !property.creditor_id && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="creditor-id-warning">
          Keine Gläubiger-ID hinterlegt. Die Gläubiger-ID wird für SEPA-Lastschriften benötigt.
        </Alert>
      )}

      {/* Property info header */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <StatusBadge type="management_type" value={property.management_type} />
          <Typography variant="body2" color="text.secondary">
            Referenz: {property.reference}
          </Typography>
          {property.address && (
            <Typography variant="body2" color="text.secondary">
              {property.address.street} {property.address.house_number}, {property.address.postal_code} {property.address.city}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Verwaltet seit: {property.management_start}
          </Typography>
        </Box>
      </Paper>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" display="block">Einheiten</Typography>
            <Typography variant="h5" fontWeight={600}>{activeUnits.length}</Typography>
            <Typography variant="body2" color="text.secondary">{units.length} gesamt</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Paper
            elevation={0}
            sx={{ p: 2, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units`)}
            data-testid="units-link"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ApartmentIcon color="primary" />
              <Typography variant="body2" fontWeight={500}>Einheiten verwalten</Typography>
            </Box>
          </Paper>
        </Grid>
        {property.management_type === 'weg' && (
          <Grid size={{ xs: 12, sm: 3 }}>
            <Paper
              elevation={0}
              sx={{ p: 2, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
              onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/assemblies`)}
            >
              <Typography variant="caption" color="text.secondary" display="block">Versammlungen</Typography>
              <Typography variant="body2" fontWeight={500}>Versammlungen</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Units table */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Eigentümer / Einheiten
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Nr.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kategorie</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Lage</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>MEA (‰)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Eigentümer / Mieter</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {units.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Noch keine Einheiten angelegt.</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/new`)}
                  >
                    Erste Einheit anlegen
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {units.map((unit) => (
              <TableRow
                key={unit.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/${unit.id}`)}
                data-testid={`unit-row-${unit.id}`}
              >
                <TableCell><Typography variant="body2" fontWeight={500}>{unit.number}</Typography></TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={CATEGORY_LABELS[unit.category] || unit.category}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
                <TableCell><Typography variant="body2">{unit.location}</Typography></TableCell>
                <TableCell><Typography variant="body2">{unit.mea}</Typography></TableCell>
                <TableCell><Typography variant="body2">{unit.current_contact_name || '–'}</Typography></TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={unit.active ? 'Aktiv' : 'Inaktiv'}
                    color={unit.active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {units.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units`)}
          >
            Alle Einheiten anzeigen
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 1 }}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/units/new`)}
          >
            Einheit anlegen
          </Button>
        </Box>
      )}
    </Box>
  )
}
